import {addVectors, midPoint, Vector} from "./vector"

/**
 * Type representing a triangle in the Sierpinski fractal.
 */
export type Triangle = {top: Vector, left: Vector, right: Vector}

/**
 * Check if the point is within the given triangle, assuming the triangle is an equilateral triangle from the
 * Sierpinski pattern.
 */
export const isPointInTriangle = ({left, top}: Triangle) => ([x, y]: Vector): boolean => {
    if (y < top[1] || y > left[1]) {
        return false
    } else {
        const gradient = (top[1] - left[1]) / (top[0] - left[0])
        return x <= top[0] + (top[1] - y) / gradient && x >= top[0] + (y - top[1]) / gradient
    }
}

export type ChildTriangles = {
    topChild: Triangle
    leftChild: Triangle
    rightChild: Triangle
}

/**
 * Divide a triangle into three child triangles derived from the original triangle's vertices and their midpoints.
 */
export const splitTriangle = ({left, right, top}: Triangle): ChildTriangles => {
    const leftMid = midPoint(left, top)
    const rightMid = midPoint(top, right)
    const baseMid = midPoint(left, right)

    return {
        topChild: {top: top, left: leftMid, right: rightMid},
        leftChild: {top: leftMid, left: left, right: baseMid},
        rightChild: {top: rightMid, left: baseMid, right: right}
    }
}

export type Rectangle = {topLeft: Vector, size: Vector}

/**
 * Returns true if the point is inside the rectangle defined by its top left point and size vector. The rectangle is
 * assumed to have a base parallel to the x axis.
 */
export const isPointInRectangle = ({topLeft, size}: Rectangle) => (point: Vector): boolean => {
    const bottomRight = addVectors(topLeft, size)
    return point[0] >= topLeft[0] && point[1] >= topLeft[1] && point[0] <= bottomRight[0] && point[1] <= bottomRight[1]
}

/**
 * Check if a rectangle and a triangle intersect, for the specific case of a rectangle with a base parallel to the x
 * axis an isosceles triangle with a base parallel to the x axis.
 */
export const rectangleTriangleIntersect = ({topLeft, size}: Rectangle, {top, left, right}: Triangle): boolean => {
    if ([top, left, right].some(isPointInRectangle({topLeft, size}))) {
        return true
    } else {
        const bottomLeft: Vector = [topLeft[0], topLeft[1] + size[1]]
        const bottomRight: Vector = addVectors(topLeft, size)
        const topRight: Vector = [topLeft[0] + size[0], topLeft[1]]

        if ([topLeft, bottomLeft, topRight, bottomRight].some(isPointInTriangle({top, left, right}))){
            return true
        } else {
            // Does the left rectangle edge intersect the triangle base?
            const baseCheck = left[0] <= topLeft[0] && right[0] >= topLeft[0] && topLeft[1] <= top[1] && bottomLeft[1] >= left[1]

            // Calculate the gradient and y intercept of the line forming the triangle's left side
            const leftGradient = (top[1] - left[1]) / (top[0] - left[0])
            const leftYIntercept = top[1] - leftGradient * top[0]

            // Does the top rectangle edge intersect the left triangle edge?
            const xIntercept = (topLeft[1] - leftYIntercept) / leftGradient
            const sideCheck = topLeft[0] <= xIntercept && xIntercept <= (topLeft[0] + size[0])
                && left[0] <= xIntercept && xIntercept <= top[0]

            /*
             * After checking that no triangle points lie in the rectangle and vice versa, it is sufficient to just
             * check if the left rectangle edge intersects the triangle base or the top rectangle edge intersects the
             * left triangle edge, given the constraints on the triangle and rectangle.
             */
            return baseCheck || sideCheck
        }
    }
}



// WEBPACK FOOTER //
// ./src/geometry.ts